import { useState } from 'react';

import dayjs from 'dayjs';

import { Theme, lighten } from '@mui/material';

import { EventSourcingEvent, UserData } from '@octopus/api';
import {
  contractFieldChanges,
  eventFieldChanges,
  isChangedFieldHidden,
} from '@octopus/formatters';

import {
  ContractHistoryEvent,
  useFormattedDiffValues,
  useGetAllAuthoredContractEvents,
} from './data';

export type { ContractHistoryEvent } from './data';

const getHoveredBackgoundColor = (theme: Theme) =>
  lighten(theme.palette.text.primary, 0.96);

function useHoverBackground(hovered = false) {
  const [isHovered, setHover] = useState<boolean>(hovered);

  const hoveredStyle = (theme: Theme) => ({
    backgroundColor: isHovered ? getHoveredBackgoundColor(theme) : undefined,
  });

  const hoverEventHandlers = {
    onMouseEnter: () => setHover(true),
    onMouseLeave: () => setHover(false),
  };

  return {
    hoveredStyle,
    hoverEventHandlers,
    isHovered,
  };
}

const SUPPORTED_HISTORY_EVENT_TYPES = new Set([
  'employeeOnAdmission',
  'employeeAdmitted',
  'contractChanged',
  'salaryChanged',
  'internalTransfer',
  'pjAdmitted',
  'pjContractChanged',
  'pjInternalTransfer',
  'companyTransfer',
  'managerChanged',
  'contractTerminated',
  'pjContractTerminated',
]);

const CANCELABLE_EVENT_TYPES = new Set<string>([
  'contractChanged',
  'salaryChanged',
  'pjContractChanged',
]);
const CANCELABLE_SCHEDULED_EVENTS_TYPES = new Set([
  'internalTransfer',
  'companyTransfer',
  'pjInternalTransfer',
  'managerChanged',
  'contractTerminated',
  'pjContractTerminated',
]);

const EVENT_TYPES_TO_ACTION_NAME: Record<string, string> = {
  employeeOnAdmission: 'Registro em',
  employeeAdmitted: 'Registro em',
  pjAdmitted: 'Registro em',
  contractTerminated: 'Desligamento em',
  pjContractTerminated: 'Inativado em',
};
const getEventActionName = (eventType: string): string =>
  EVENT_TYPES_TO_ACTION_NAME[eventType] ?? 'Alteração em';

const eventIsScheduled = <T extends EventSourcingEvent>(event: T) =>
  dayjs().isBefore(dayjs(event.effectiveDate), 'day');

const supportedHistoryEventsSorted = <T extends EventSourcingEvent>(
  events: T[],
): T[] =>
  events
    .filter((e) => SUPPORTED_HISTORY_EVENT_TYPES.has(e.type))
    .sort((a, b) => -a.effectiveDate.localeCompare(b.effectiveDate));

const addPreviousEffectDate = (
  sortedEvents: ContractHistoryEvent[], // must be sorted by effectiveDate desc
): ContractHistoryEvent[] => {
  if (sortedEvents.length === 0 || sortedEvents.length === 1) {
    return sortedEvents;
  }

  // algorithm:
  // - allocate a "buffer" that will hold all events that have the same effectiveDate
  // - iterate forward until the effectiveDate changes
  // - set `previousEffectiveDate` of all events in the buffer as the current event's effetiveDate
  // (since it's sorted by `effectiveDate desc` it works)
  // - flush the buffer by appending into a result buffer
  // - when iteration over the array finishes:
  //   -- flush the last event buffer (earliest effectiveDate) into the result buffer
  //   -- return result

  let currentBatch: ContractHistoryEvent[] = [];
  let currentEffectiveDate: string = sortedEvents[0].effectiveDate;
  let result: ContractHistoryEvent[] = [];

  for (const event of sortedEvents) {
    if (event.effectiveDate !== currentEffectiveDate) {
      result = result.concat(
        currentBatch.map((e) =>
          Object.assign(e, {
            previousEffectiveDate: event.effectiveDate,
          }),
        ),
      );
      currentBatch = [];
      currentEffectiveDate = event.effectiveDate;
    }
    currentBatch.push(event);
  }
  result = result.concat(currentBatch);

  return result;
};

const canCancelEvent = (event: ContractHistoryEvent) => {
  if (event.canceled) {
    return false;
  }
  // older than a month: false
  if (dayjs().diff(dayjs(event.effectiveDate), 'M') >= 1) {
    return false;
  }
  // not older than a month
  // cancelable regardless of effectiveDate
  if (CANCELABLE_EVENT_TYPES.has(event.type)) {
    return true;
  }
  // cancelable if effectiveDate is in the future
  if (CANCELABLE_SCHEDULED_EVENTS_TYPES.has(event.type)) {
    return eventIsScheduled(event);
  }
  return false;
};

const SYSTEM_USER_NAME = 'Tako';
const SYSTEM_USER_EMAIL_DOMAINS = ['@8arm.io', '@ext.usetako.com'];
const getAuthorName = (user?: UserData) =>
  !user
    ? SYSTEM_USER_NAME
    : SYSTEM_USER_EMAIL_DOMAINS.some((domain) =>
          user.userData.email.endsWith(domain),
        )
      ? SYSTEM_USER_NAME
      : user.userData.name;

const getPrimaryLighter = (theme: Theme) => theme.palette.strokes.primary;

const getGrayscaleLighter = (theme: Theme) => theme.palette.strokes.heavy;

const getBackGroundColor = (_: Theme) => '#EBEBEB';

export const Utils = {
  Hooks: {
    useHoverBackground,
    useGetAllAuthoredContractEvents,
    useFormattedDiffValues,
  },
  Events: {
    eventIsScheduled,
    getEventActionName,
    supportedHistoryEventsSorted,
    addPreviousEffectDate,
    getAuthorName,
    contractFieldChanges,
    eventFieldChanges,
    isChangedFieldHidden,
    canCancelEvent,
  },
  Styles: {
    getPrimaryLighter,
    getGrayscaleLighter,
    getHoveredBackgoundColor,
    getBackGroundColor,
  },
};
